import type { JsonValue, RecordDefinition, Transaction } from "@e308/core";
import type { SeatModel, SeatResources } from "./content-types.js";
import { formatMicro, type Micro, microFromString } from "./exact-numbers.js";
import { borderIsActive } from "./lifecycle-state.js";
import type { MatchLifecycleState } from "./lifecycle-types.js";
import { advanceAutoReinforce, advanceLogistics } from "./logistics-runtime.js";
import { addTroops } from "./logistics-state.js";
import type { TroopComposition } from "./logistics-types.js";
import { UNIT_IDS } from "./logistics-types.js";
import { dispatchReinforcements } from "./reinforcement-runtime.js";
import { effectMultiplier, effectsForResearch } from "./research-effects.js";
import { unitResource } from "./seat-resources.js";

export function runTransitStep(
  transaction: Transaction<Micro>,
  seats: readonly SeatModel[],
  stepMs: number,
  lifecycleDefinition?: RecordDefinition<MatchLifecycleState & JsonValue>,
): void {
  for (const seat of seats) {
    const advanced = advanceLogistics(
      transaction.getRecord(seat.logistics),
      transaction.gameTimeMs(),
      travelMultiplier(transaction, seat),
      stepMs,
      liveBorders(transaction, seat, seats, lifecycleDefinition),
    );
    setHomeTroops(
      transaction,
      seat.resources,
      addTroops(homeTroops(transaction, seat.resources), advanced.returned),
    );
    transaction.setRecord(seat.logistics, advanced.state);
  }
}

export function runAutoReinforceStep(
  transaction: Transaction<Micro>,
  seats: readonly SeatModel[],
  stepMs: number,
  lifecycleDefinition?: RecordDefinition<MatchLifecycleState & JsonValue>,
): void {
  for (const seat of seats) {
    if (transaction.get(seat.resources.headquarters) <= 0n) continue;
    const current = transaction.getRecord(seat.logistics);
    if (current.reinforcement) {
      const dispatched = dispatchReinforcements(
        current,
        transaction.gameTimeMs(),
        travelMultiplier(transaction, seat),
        stepMs,
        liveBorders(transaction, seat, seats, lifecycleDefinition),
      );
      setHomeTroops(
        transaction,
        seat.resources,
        addTroops(homeTroops(transaction, seat.resources), dispatched.returned),
      );
      transaction.setRecord(seat.logistics, dispatched.state as typeof current);
      continue;
    }
    const reinforced = advanceAutoReinforce(
      transaction.getRecord(seat.logistics),
      homeTroops(transaction, seat.resources),
      transaction.gameTimeMs(),
      travelMultiplier(transaction, seat),
      stepMs,
      liveBorders(transaction, seat, seats, lifecycleDefinition),
    );
    setHomeTroops(transaction, seat.resources, reinforced.home);
    transaction.setRecord(seat.logistics, reinforced.state);
  }
}

function liveBorders(
  transaction: Transaction<Micro>,
  seat: SeatModel,
  seats: readonly SeatModel[],
  lifecycleDefinition?: RecordDefinition<MatchLifecycleState & JsonValue>,
): ReadonlySet<string> {
  const lifecycle = lifecycleDefinition ? transaction.getRecord(lifecycleDefinition) : undefined;
  return new Set(
    seat.borderIds.filter((id) => {
      const target = seats.find((candidate) => candidate.seat.id === id);
      return (
        target &&
        transaction.get(target.resources.headquarters) > 0n &&
        (!lifecycle || borderIsActive(lifecycle, seat.seat.id, id))
      );
    }),
  );
}

function travelMultiplier(transaction: Transaction<Micro>, seat: SeatModel): string {
  return effectMultiplier(
    effectsForResearch(transaction.getRecord(seat.research)),
    "military-travel-time",
  );
}

export function homeTroops(
  transaction: Transaction<Micro>,
  resources: SeatResources,
): TroopComposition {
  return Object.freeze(
    Object.fromEntries(
      UNIT_IDS.map((id) => [id, formatMicro(transaction.get(unitResource(resources, id)))]),
    ),
  ) as TroopComposition;
}

export function setHomeTroops(
  transaction: Transaction<Micro>,
  resources: SeatResources,
  troops: TroopComposition,
): void {
  for (const id of UNIT_IDS)
    transaction.set(unitResource(resources, id), microFromString(troops[id]));
}
